import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IauthState } from "./auth.types";
import axiosInstance from "config/axios";
import { OC_TOKEN } from "utils/constants";

const initialState: IauthState = {
  auth: {
    userID: "",
    token: "",
    fullName: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: 0,
    address: "",
    address2: "",
    city: "",
    country: "",
    state: "",
    zipcode: "",
  },
  error: {},
  isActivityInProcess: false,
  loginSuccessMesssage: "",
  isValidToken: false,
  tokenMessage: "",
};

export const login = createAsyncThunk(
  "login/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().post("/auth/login", user);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const signup = createAsyncThunk(
  "signup/user",
  async (
    { user, hospital }: { user: any; hospital: boolean },
    { rejectWithValue }
  ) => {
    try {
      user.isHospital = hospital;
      const { data } = await axiosInstance().post(`/auth/register`, user);

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "forgotPass/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().post(
        `/auth/forget-password`,
        user
      );

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const newPassword = createAsyncThunk(
  "newPass/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().post(
        `/auth/verify-forget-password`,
        user
      );

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const verifyEmail = createAsyncThunk(
  "verifyEmail/user",
  async (otp: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().post(`/auth/verify-email`, otp);

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const verifyToken = createAsyncThunk(
  "verifyToken/user",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().get(`/auth/verify-token`);

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

const authSlice = createSlice({
  name: "authSlice",
  initialState: { ...initialState, name: "authInitialState" },
  reducers: {
    errorCleanUp: (state) => {
      state.error = {
        code: null,
        message: "",
        error: "",
      };
    },
    messageCleanUp: (state) => {
      state.loginSuccessMesssage = "";
    },
    tokenCleaner: (state) => {
      state.isValidToken = false;
      localStorage.setItem(OC_TOKEN, "");
    },
  },
  extraReducers: (builder) => {
    // signup cases
    builder.addCase(signup.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(signup.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(signup.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.loginSuccessMesssage = action.payload.message;
    });

    // verify email cases
    builder.addCase(verifyEmail.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(verifyEmail.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(verifyEmail.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.loginSuccessMesssage = action.payload.message;
    });

    // verify token cases
    builder.addCase(verifyToken.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(verifyToken.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action.payload?.status,
        message: action.payload.data.message,
      };
      state.isValidToken = false;
    });

    builder.addCase(verifyToken.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.tokenMessage = action.payload.message;

      state.isValidToken = true;
    });

    // login cases
    builder.addCase(login.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(login.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action?.payload?.status,
        message: action?.payload?.data?.message,
      };
    });

    builder.addCase(login.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.loginSuccessMesssage = action.payload.message;

      localStorage.setItem(OC_TOKEN, action.payload.token);
    });

    // forgot password cases
    builder.addCase(forgotPassword.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(forgotPassword.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(forgotPassword.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.loginSuccessMesssage = action.payload.message;
    });

    // new password cases
    builder.addCase(newPassword.pending, (state, action: any) => {
      state.isActivityInProcess = true;
    });

    builder.addCase(newPassword.rejected, (state, action: any) => {
      state.isActivityInProcess = false;
      state.error = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(newPassword.fulfilled, (state, action: any) => {
      state.isActivityInProcess = false;
      state.loginSuccessMesssage = action.payload.message;
    });
  },
});
export const { errorCleanUp, messageCleanUp, tokenCleaner } = authSlice.actions;

export default authSlice.reducer;
